import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { Rive } from '@rive-app/canvas'
import type { EliteLandingPlan } from '../planTypes'
import RiveComponent, { RiveAlignment, RiveFit } from '../../../components/RiveComponent'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { useNewEliteLandingAnalyticsScreen } from '../analytics/NewEliteLandingAnalyticsContext'
import {
  NewEliteLandingBottomSheetName,
  NewEliteLandingEventId,
  trackNewEliteUserClicked
} from '../analytics/newEliteLandingAnalytics'
import {
  BenefitsInfoBottomSheet,
  eliteBenefitInfoTabTitles
} from './NewEliteLandingPageBenefits'

import { useEliteBuyCtaExtras } from '../../../components/elite/EliteBuyCtaExtrasContext'
const ASSETS = {
  cornerAccent: '/images/new-elite/testimonials/union.svg'
} as const

const keyFeatureRiveTimelineName = 'Timeline 1'

function playKeyFeatureRiveTimelineInView(rive: Rive, timelineName: string) {
  rive.play(timelineName)
}

type KeyFeatureCardModel = {
  id: string
  riveSrc: string
  comingSoon?: boolean
  titleBold: string
  titleLine1Rest?: string
  titleLine2?: string
  body: string
}

const keyFeatureCards: KeyFeatureCardModel[] = [
  {
    id: 'power-rank',
    riveSrc: '/rives/new-elite/Rank.riv',
    titleBold: 'Power Rank',
    titleLine1Rest: 'for effortless',
    titleLine2: ' fund selection',
    body: 'Always invest in top ranked funds, without the guesswork.'
  },
  {
    id: 'power-select',
    riveSrc: '/rives/new-elite/Select.riv',
    titleBold: 'Power Select',
    titleLine1Rest: 'for comprehensive fund comparison',
    body: 'Compare funds within a category and pick the ones that best fit your portfolio.'
  },
  {
    id: 'rank-trends',
    riveSrc: '/rives/new-elite/Rank_trend.riv',
    titleBold: 'Rank Trends',
    titleLine1Rest: 'for clearer ',
    titleLine2: 'performance tracking',
    body: 'Track how a fund’s rank has changed over the last 24 months, not just today.'
  },
  {
    id: 'power-rebalance',
    riveSrc: '/rives/new-elite/Rebalance.riv',
    titleBold: 'Power Rebalance',
    titleLine1Rest: 'for guided portfolio rebalancing',
    body: 'Exit underperforming funds and switch to stronger ones with a tax-efficient plan.'
  },
  {
    id: 'regular-to-direct-plan',
    riveSrc: '/rives/new-elite/R2D.riv',
    titleBold: 'Regular to Direct Plan',
    titleLine1Rest: 'for easy savings on high expense ratios',
    body: `Identify extra costs in your Regular funds, and switch to Direct funds tax-efficiently.`
  },
  {
    id: 'elite-lens',
    riveSrc: '/rives/new-elite/Lens.riv',
    titleBold: 'Elite Lens',
    titleLine1Rest: 'for insights that help you invest with conviction.',
    body: 'Get insights into concepts that change the way you look at investing.'
  },
  {
    id: 'tax-saving-plan',
    riveSrc: '/rives/new-elite/taxharvesting.riv',
    titleBold: 'Tax Saving Plan',
    titleLine1Rest: 'for simplified tax planning',
    body: 'Make the most of your yearly tax benefits by utilising the ₹1.25L LTCG exemption.'
  },
  // {
  //   id: 'power-allocation',
  //   riveSrc: '/rives/new-elite/Allocation.riv',
  //   comingSoon: true,
  //   titleBold: 'Power Allocation',
  //   titleLine1Rest: 'for a portfolio aligned with your financial goals',
  //   body: "Invest with a strategy tailored to your goals and risk profile."
  // },
  // {
  //   id: 'sip-strategy',
  //   riveSrc: '/rives/new-elite/SIP_recommendation.riv',
  //   comingSoon: true,
  //   titleBold: 'SIP Strategy',
  //   titleLine1Rest: 'for investing regularly with a plan',
  //   body: "Invest in the right funds every month, as per your ideal portfolio strategy."
  // }
]

function keyFeatureCardToBenefitTabTitle(card: KeyFeatureCardModel): string {
  if (card.id === 'regular-to-direct-plan') {
    return 'Regular to Direct plan'
  }
  return card.titleBold
}

type KeyFeaturesCarouselScrollRootContextValue = {
  scrollRootElement: HTMLDivElement | null
}

const KeyFeaturesCarouselScrollRootContext = createContext<KeyFeaturesCarouselScrollRootContextValue | null>(null)

type NewEliteLandingKeyFeaturesCarouselProps = {
  selectedPlan: EliteLandingPlan
}

export default function NewEliteLandingKeyFeaturesCarousel({ selectedPlan }: NewEliteLandingKeyFeaturesCarouselProps) {
  const [scrollRootElement, setScrollRootElement] = useState<HTMLDivElement | null>(null)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const sectionSubtitle =
    selectedPlan === 'yearly'
      ? 'Features exclusive to PowerUp Elite members'
      : 'Same Elite features on flexible monthly billing — upgrade or switch anytime.'

  const openKeyFeatureLearnMore = (card: KeyFeatureCardModel) => {
    trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.KeyFeatureLearnMore, {
      ...eliteBuyCtaExtras,
      buttonLabel: keyFeatureCardToBenefitTabTitle(card),
      bottomSheetName: NewEliteLandingBottomSheetName.BenefitsInfo
    })
    openBottomSheet(
      <BenefitsInfoBottomSheet
        onDismiss={closeBottomSheet}
        initialTabTitle={keyFeatureCardToBenefitTabTitle(card)}
        tabTitles={eliteBenefitInfoTabTitles}
        buyCtaExtras={eliteBuyCtaExtras}
      />
    )
  }

  return (
    <div className='mb-10 flex flex-col gap-8'>
      <div className='flex flex-col px-1'>
        <p className='text-lg font-medium text-[#232323]'>Key features</p>
        <p className='max-w-[277px] text-sm leading-[1.4] text-[#6A6E6E]'>{sectionSubtitle}</p>
      </div>

      <div className='-mx-5'>
        <KeyFeaturesCarouselScrollRootContext.Provider value={{ scrollRootElement }}>
          <div
            ref={setScrollRootElement}
            className='flex snap-x snap-mandatory gap-5 overflow-x-auto overflow-y-hidden px-5 pb-1 no-scrollbar [scroll-padding-inline:20px]'
          >
            {keyFeatureCards.map((card) => (
              <KeyFeatureCarouselCard key={card.id} card={card} onLearnMore={() => openKeyFeatureLearnMore(card)} />
            ))}
          </div>
        </KeyFeaturesCarouselScrollRootContext.Provider>
      </div>
    </div>
  )
}

export function KeyFeatureCarouselCard({ card, onLearnMore }: { card: KeyFeatureCardModel; onLearnMore: () => void }) {
  return (
    <div className='relative w-[min(304px,calc(100vw-40px))] shrink-0 snap-start'>
      <div className='relative flex h-[420px] flex-col rounded-2xl border-[1.5px] border-[#F0F4F7] pb-4'>
        <div className='h-[208px] overflow-hidden rounded-t-[20px] bg-white px-0 pt-0'>
          <KeyFeatureCardRive src={card.riveSrc} timelineName={keyFeatureRiveTimelineName} />
        </div>

        <div className='flex flex-1 flex-col gap-2 px-4 pt-6'>
          {card.comingSoon ? (
            <div className='flex justify-start'>
              <p className='text-xs font-medium text-[#CC5C2D]'>Coming soon</p>
            </div>
          ) : null}

          <div className='text-base leading-[1.4] text-black'>
            <p>
              <span className='font-bold'>{card.titleBold}</span>
              {card.titleLine1Rest ? <span className='text-black/80'> {card.titleLine1Rest}</span> : null}
            </p>
            {card.titleLine2 ? <p className='text-black/80'>{card.titleLine2}</p> : null}
          </div>

          <div className='text-[13px] leading-[1.52] text-[#6A6E6E]'>
            <p>{card.body}</p>
          </div>
          <div onClick={onLearnMore} className='mt-3 inline cursor-pointer underline pt-5 text-13 text-[#6A6E6E]'>
            Learn more
          </div>
        </div>
        <div className='absolute bottom-[-6px] right-[-8px] bg-white z-[1] p-2'>
          <img alt='' className='pointer-events-none size-6 object-contain' src={ASSETS.cornerAccent} />
        </div>
      </div>
    </div>
  )
}

type KeyFeatureCardRiveProps = {
  src: string
  timelineName: string
}

function KeyFeatureCardRive({ src, timelineName }: KeyFeatureCardRiveProps) {
  const scrollCtx = useContext(KeyFeaturesCarouselScrollRootContext)
  const scrollRootElement = scrollCtx?.scrollRootElement ?? null
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const riveRef = useRef<Rive | null>(null)
  const hasPlayedTimelineInViewRef = useRef(false)
  const [riveReady, setRiveReady] = useState(false)
  const [heroInCarouselView, setHeroInCarouselView] = useState(false)

  useEffect(() => {
    const root = scrollRootElement
    const target = wrapRef.current
    if (!root || !target) return
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0]
        if (e?.isIntersecting) {
          setHeroInCarouselView(true)
          obs.disconnect()
        }
      },
      { root, threshold: 1.0 }
    )
    obs.observe(target)
    return () => obs.disconnect()
  }, [scrollRootElement])

  useEffect(() => {
    if (!heroInCarouselView || !riveReady || hasPlayedTimelineInViewRef.current || !riveRef.current) return
    const instance = riveRef.current
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!instance || hasPlayedTimelineInViewRef.current) return
        playKeyFeatureRiveTimelineInView(instance, timelineName)
        hasPlayedTimelineInViewRef.current = true
      })
    })
  }, [heroInCarouselView, riveReady, timelineName])

  return (
    <div ref={wrapRef} className='h-full w-full'>
      <RiveComponent
        key={src}
        src={src}
        animations={timelineName}
        autoplay={false}
        fit={RiveFit.WIDTH}
        alignment={RiveAlignment.CENTER}
        className='h-full w-full'
        style={{ backgroundColor: '#ffffff' }}
        onLoad={(instance) => {
          riveRef.current = instance
          setRiveReady(true)
        }}
      />
    </div>
  )
}
